import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Users, Search, Loader, ArrowDown, ArrowUp, ArrowUpDown, Shield, User, AlertTriangle, X } from 'lucide-react';
import EmployeePhoto from './EmployeePhoto';
import ScrollToTop from './ScrollToTop';
import { api } from '../services/api';
import type { Employee, PagedResult, EmployeesTabProps } from '../types';

const PAGE_SIZE = 30;

type SortField = 'name' | 'title' | 'department' | 'manager';
type SortDirection = 'asc' | 'desc';

const EmployeesTab: React.FC<EmployeesTabProps> = ({ currentUserId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [updatingRole, setUpdatingRole] = useState<number | null>(null);
  const [roleConfirm, setRoleConfirm] = useState<{ employeeId: number; employeeName: string; oldRole: string; newRole: 'admin' | 'user' } | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);

  // Sorting state
  const [sortField, setSortField] = useState<SortField | null>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Scroll-to-top handled by <ScrollToTop /> component

  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback(
    (node: HTMLElement | null) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore]
  );

  // Debounce search input by 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset when search or sort changes
  useEffect(() => {
    setEmployees([]);
    setPage(1);
    setHasMore(true);
    setInitialLoading(true);
  }, [debouncedSearch, sortField, sortDirection]);

  // Fetch data when page, search, or sort changes
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const result: PagedResult<Employee> = await api.getEmployeesPaged(
          page,
          PAGE_SIZE,
          debouncedSearch || undefined,
          sortField || undefined,
          sortField ? sortDirection : undefined
        );
        if (!cancelled) {
          setEmployees((prev) => (page === 1 ? result.items : [...prev, ...result.items]));
          setTotalCount(result.totalCount);
          setHasMore(page < result.totalPages);
        }
      } catch (err) {
        console.error('Error fetching employees:', err);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setInitialLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle between asc and desc
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleRoleChange = (employeeId: number, newRole: 'admin' | 'user') => {
    const employee = employees.find(emp => emp.employeeID === employeeId);
    if (!employee) return;
    setRoleConfirm({ employeeId, employeeName: employee.name, oldRole: employee.role, newRole });
  };

  const confirmRoleChange = async () => {
    if (!roleConfirm) return;
    const { employeeId, newRole } = roleConfirm;
    setRoleConfirm(null);
    setUpdatingRole(employeeId);
    try {
      await api.updateEmployeeRole(employeeId, newRole);
      setEmployees(prev =>
        prev.map(emp =>
          emp.employeeID === employeeId
            ? { ...emp, role: newRole }
            : emp
        )
      );
    } catch (err) {
      console.error('Error updating employee role:', err);
      setRoleError(err instanceof Error ? err.message : 'Failed to update employee role. Please try again.');
    } finally {
      setUpdatingRole(null);
    }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 text-blue-600" />
      : <ArrowDown className="w-3.5 h-3.5 text-blue-600" />;
  };

  const columnHeaderClass = (field: SortField) =>
    `px-6 py-4 text-left text-xs font-bold uppercase tracking-wider cursor-pointer select-none hover:bg-gray-200/50 transition-colors ${
      sortField === field ? 'text-blue-700' : 'text-gray-700'
    }`;

  return (
    <div className="space-y-6">
      {/* Responsive: header stacks on mobile, search input full-width */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Employee Directory</h2>
          <p className="text-gray-600 mt-1">{totalCount} total employees</p>
        </div>
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, title, department..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-80 shadow-sm min-h-[44px] text-base"
          />
        </div>
      </div>

      {initialLoading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">Loading employees...</p>
        </div>
      )}

      {!initialLoading && employees.length > 0 && (
        <>
          {/* Mobile: card-based layout */}
          <div className="md:hidden space-y-3">
            {employees.map((emp) => {
              const isUpdating = updatingRole === emp.employeeID;
              const isCurrentUser = currentUserId !== undefined && currentUserId > 0 && emp.employeeID === currentUserId;
              return (
                <div
                  key={emp.employeeID}
                  className="bg-white rounded-xl shadow-md p-4 border border-gray-100"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <EmployeePhoto photoURL={emp.photoURL} name={emp.name} size="small" />
                    <div className="min-w-0 flex-1">
                      <span className="font-semibold text-gray-900 block truncate">{emp.name}</span>
                      {emp.email && <p className="text-xs text-gray-400 truncate">{emp.email}</p>}
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                      emp.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {emp.role === 'admin' ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                      {emp.role.charAt(0).toUpperCase() + emp.role.slice(1)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {emp.title && (
                      <div>
                        <p className="text-xs text-gray-400 font-medium">Title</p>
                        <p className="text-gray-700 truncate">{emp.title}</p>
                      </div>
                    )}
                    {emp.department && (
                      <div>
                        <p className="text-xs text-gray-400 font-medium">Department</p>
                        <p className="text-gray-600 truncate">{emp.department}</p>
                      </div>
                    )}
                    {emp.manager && (
                      <div>
                        <p className="text-xs text-gray-400 font-medium">Manager</p>
                        <p className="text-gray-500 truncate">{emp.manager}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-1">Role</p>
                      <select
                        value={emp.role}
                        onChange={(e) => handleRoleChange(emp.employeeID, e.target.value as 'admin' | 'user')}
                        disabled={isUpdating || isCurrentUser}
                        title={isCurrentUser ? 'You cannot change your own role' : ''}
                        className={`w-full px-2 py-1.5 border rounded-lg text-sm font-medium min-h-[44px] ${
                          emp.role === 'admin'
                            ? 'bg-purple-50 border-purple-300 text-purple-700'
                            : 'bg-gray-50 border-gray-300 text-gray-700'
                        } ${isUpdating ? 'opacity-50 cursor-wait' : isCurrentUser ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
            {loading && (
              <div className="flex items-center justify-center gap-2 py-4">
                <Loader className="w-4 h-4 text-blue-600 animate-spin" />
                <span className="text-sm text-gray-500">Loading more...</span>
              </div>
            )}
          </div>

          {/* Desktop: table layout */}
          <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <table className="w-full table-fixed">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                <tr>
                  <th style={{ width: '25%' }} className={columnHeaderClass('name')} onClick={() => handleSort('name')}>
                    <div className="flex items-center gap-1.5">
                      Name
                      <SortIcon field="name" />
                    </div>
                  </th>
                  <th style={{ width: '20%' }} className={columnHeaderClass('title')} onClick={() => handleSort('title')}>
                    <div className="flex items-center gap-1.5">
                      Title
                      <SortIcon field="title" />
                    </div>
                  </th>
                  <th style={{ width: '20%' }} className={columnHeaderClass('department')} onClick={() => handleSort('department')}>
                    <div className="flex items-center gap-1.5">
                      Department
                      <SortIcon field="department" />
                    </div>
                  </th>
                  <th style={{ width: '20%' }} className={columnHeaderClass('manager')} onClick={() => handleSort('manager')}>
                    <div className="flex items-center gap-1.5">
                      Manager
                      <SortIcon field="manager" />
                    </div>
                  </th>
                  <th style={{ width: '15%' }} className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                    Role
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {employees.map((emp) => {
                  const isUpdating = updatingRole === emp.employeeID;
                  const isCurrentUser = currentUserId !== undefined && currentUserId > 0 && emp.employeeID === currentUserId;
                  return (
                    <tr
                      key={emp.employeeID}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <EmployeePhoto photoURL={emp.photoURL} name={emp.name} size="small" />
                          <div className="min-w-0">
                            <span className="font-semibold text-gray-900 block truncate">{emp.name}</span>
                            {emp.email && (
                              <p className="text-xs text-gray-400 truncate">{emp.email}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <span className="text-sm text-gray-700 font-medium block truncate" title={emp.title}>{emp.title}</span>
                      </td>
                      <td className="px-6 py-3">
                        <span className="text-sm text-gray-600 block truncate" title={emp.department}>{emp.department}</span>
                      </td>
                      <td className="px-6 py-3">
                        <span className="text-sm text-gray-500 block truncate" title={emp.manager || ''}>{emp.manager || '—'}</span>
                      </td>
                      <td className="px-6 py-3">
                        <div className="relative">
                          <select
                            value={emp.role}
                            onChange={(e) => handleRoleChange(emp.employeeID, e.target.value as 'admin' | 'user')}
                            disabled={isUpdating || isCurrentUser}
                            title={isCurrentUser ? 'You cannot change your own role' : ''}
                            className={`w-full pl-8 pr-8 py-1.5 border rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                              emp.role === 'admin'
                                ? 'bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100'
                                : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                            } ${isUpdating ? 'opacity-50 cursor-wait' : isCurrentUser ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <option value="user" style={{ backgroundColor: '#f9fafb', color: '#374151' }}>User</option>
                            <option value="admin" style={{ backgroundColor: '#faf5ff', color: '#7c3aed' }}>Admin</option>
                          </select>
                          <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
                            {emp.role === 'admin' ? (
                              <Shield className="w-4 h-4 text-purple-600" />
                            ) : (
                              <User className="w-4 h-4 text-gray-500" />
                            )}
                          </div>
                          {isUpdating && (
                            <div className="absolute inset-y-0 right-8 flex items-center pointer-events-none">
                              <Loader className="w-4 h-4 text-blue-600 animate-spin" />
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {loading && (
              <div className="flex items-center justify-center gap-2 py-4 border-t border-gray-100">
                <Loader className="w-4 h-4 text-blue-600 animate-spin" />
                <span className="text-sm text-gray-500">Loading more...</span>
              </div>
            )}
          </div>
          {/* Shared infinite-scroll sentinel — visible in both mobile and desktop layouts */}
          {hasMore && <div ref={sentinelRef} className="h-4" />}
        </>
      )}

      {!initialLoading && employees.length === 0 && (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-100">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Employees Found</h3>
          <p className="text-gray-600">
            {searchTerm
              ? `No employees match "${searchTerm}"`
              : 'No employees in the system'}
          </p>
        </div>
      )}

      {/* Role Change Confirmation Modal */}
      {roleConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 max-w-md w-full border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Confirm Role Change</h3>
            </div>
            <p className="text-gray-600 mb-2">
              You are changing <span className="font-semibold text-gray-900">{roleConfirm.employeeName}</span>&apos;s role:
            </p>
            <div className="flex items-center gap-3 mb-6 px-4 py-3 bg-gray-50 rounded-lg">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                roleConfirm.oldRole === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-200 text-gray-700'
              }`}>
                {roleConfirm.oldRole === 'admin' ? <Shield className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
                {roleConfirm.oldRole.charAt(0).toUpperCase() + roleConfirm.oldRole.slice(1)}
              </span>
              <span className="text-gray-400 font-medium">&rarr;</span>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                roleConfirm.newRole === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-200 text-gray-700'
              }`}>
                {roleConfirm.newRole === 'admin' ? <Shield className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
                {roleConfirm.newRole.charAt(0).toUpperCase() + roleConfirm.newRole.slice(1)}
              </span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setRoleConfirm(null)}
                className="flex-1 px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRoleChange}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-md"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Change Error Toast */}
      {roleError && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm bg-red-50 border border-red-200 rounded-xl shadow-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-800">Role Update Failed</p>
            <p className="text-sm text-red-600 mt-0.5">{roleError}</p>
          </div>
          <button onClick={() => setRoleError(null)} className="p-1 hover:bg-red-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-red-400" />
          </button>
        </div>
      )}

      {/* Scroll to top button — appears after scrolling down, auto-hides after 3s idle */}
      <ScrollToTop />
    </div>
  );
};

export default EmployeesTab;
